(() => {
  const SELECTOR = '[data-tld-root]';

  const campaignContext = () => {
    const params = new URLSearchParams(window.location.search);
    return {
      campaign_id: 'labor_day_2026',
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_content: params.get('utm_content')
    };
  };

  const pushEvent = (name, detail = {}) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: name, campaign: 'labor_day_2026', ...campaignContext(), ...detail });
  };

  const formatUnit = (value) => String(Math.max(0, value)).padStart(2, '0');
  const formatMoney = (cents) => {
    const currency = window.Shopify?.currency?.active || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency', currency, maximumFractionDigits: 2
      }).format(cents / 100);
    } catch (error) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  };

  const initCountdown = (root) => {
    const countdown = root.querySelector('[data-countdown]');
    const endAt = Date.parse(root.dataset.endDate || '');
    if (!countdown || Number.isNaN(endAt)) return;
    const nodes = {
      days: countdown.querySelector('[data-days]'), hours: countdown.querySelector('[data-hours]'),
      minutes: countdown.querySelector('[data-minutes]'), seconds: countdown.querySelector('[data-seconds]'),
      label: countdown.querySelector('[data-countdown-label]')
    };
    let lastAnnouncedMinute = null;
    let timer;
    const update = () => {
      const remaining = Math.max(0, endAt - Date.now());
      const totalSeconds = Math.floor(remaining / 1000);
      const days = Math.floor(totalSeconds / 86400);
      const hours = Math.floor((totalSeconds % 86400) / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      nodes.days.textContent = formatUnit(days); nodes.hours.textContent = formatUnit(hours);
      nodes.minutes.textContent = formatUnit(minutes); nodes.seconds.textContent = formatUnit(seconds);
      if (minutes !== lastAnnouncedMinute) {
        countdown.setAttribute('aria-label', `${days} days, ${hours} hours and ${minutes} minutes remaining in the Labor Day event`);
        lastAnnouncedMinute = minutes;
      }
      if (remaining === 0) {
        countdown.classList.add('is-expired');
        if (nodes.label) nodes.label.textContent = 'This offer has ended';
        window.clearInterval(timer);
      }
    };
    update(); timer = window.setInterval(update, 1000);
  };

  const copyCode = async (root, button) => {
    const code = button.dataset.copyCode || root.dataset.discountCode || '';
    const label = button.querySelector('[data-copy-label]');
    const status = root.querySelector('[data-copy-status]');
    if (!code) return;
    try {
      if (navigator.clipboard && window.isSecureContext) await navigator.clipboard.writeText(code);
      else {
        const input = document.createElement('textarea'); input.value = code; input.setAttribute('readonly', '');
        input.style.position = 'fixed'; input.style.opacity = '0'; document.body.appendChild(input);
        input.select(); document.execCommand('copy'); input.remove();
      }
      if (label) label.textContent = 'Copied';
      if (status) status.textContent = `Discount code ${code} copied.`;
      pushEvent('tesevo_labor_day_copy_code', { discount_code: code });
      window.setTimeout(() => { if (label) label.textContent = 'Copy code'; }, 2200);
    } catch (error) {
      if (label) label.textContent = code;
      if (status) status.textContent = `Copy failed. Enter code ${code} at checkout.`;
    }
  };

  const initDiscountState = (root) => {
    const appliedCode = new URLSearchParams(window.location.search).get('discount_applied');
    const status = root.querySelector('[data-discount-status]');
    if (!status || appliedCode !== root.dataset.discountCode) return;
    status.hidden = false;
    status.textContent = `${appliedCode} is ready and will be applied to eligible items at checkout.`;
    pushEvent('tesevo_labor_day_discount_applied', { discount_code: appliedCode, discount_auto_applied: true });
  };

  const preserveAttributionInDiscountLinks = (root) => {
    const current = new URLSearchParams(window.location.search);
    const keys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'];
    try {
      const discountUrl = new URL(root.dataset.discountUrl, window.location.origin);
      const redirectUrl = new URL(discountUrl.searchParams.get('redirect') || window.location.pathname, window.location.origin);
      redirectUrl.searchParams.set('discount_applied', root.dataset.discountCode);
      keys.forEach((key) => { if (current.get(key)) redirectUrl.searchParams.set(key, current.get(key)); });
      discountUrl.searchParams.set('redirect', `${redirectUrl.pathname}${redirectUrl.search}${redirectUrl.hash}`);
      root.dataset.discountUrl = `${discountUrl.pathname}${discountUrl.search}`;
      root.querySelectorAll('[data-auto-discount]').forEach((link) => { link.href = root.dataset.discountUrl; });
    } catch (error) {
      // Keep the server-rendered Shopify discount URL as the safe fallback.
    }
  };

  const eligibleVariantIds = (root) => new Set((root.dataset.eligibleVariantIds || '').split(',').map((value) => value.trim()).filter(Boolean));
  const getCart = async (root) => {
    const response = await fetch(root.dataset.cartUrl || '/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`Cart request failed with ${response.status}`);
    return response.json();
  };
  const eligibleSubtotal = (cart, ids) => (cart.items || []).reduce((total, item) => {
    if (!ids.has(String(item.variant_id || item.id))) return total;
    return total + Number(item.original_line_price ?? item.line_price ?? 0);
  }, 0);

  const renderCartProgress = (root, cart) => {
    const panel = root.querySelector('[data-cart-progress]');
    if (!panel) return;
    const threshold = Number(root.dataset.discountThreshold || 12900);
    const eligible = eligibleSubtotal(cart, eligibleVariantIds(root));
    const gap = Math.max(0, threshold - eligible);
    const percent = Math.min(100, Math.round((eligible / threshold) * 100));
    const copy = panel.querySelector('[data-cart-progress-copy]');
    const detail = panel.querySelector('[data-cart-progress-detail]');
    const bar = panel.querySelector('[data-cart-progress-bar]');
    const cta = panel.querySelector('[data-progress-cta]');
    if (bar) bar.style.width = `${percent}%`;
    panel.classList.toggle('is-unlocked', gap === 0 && eligible > 0);
    if (eligible === 0) {
      copy.textContent = `Build an eligible ${formatMoney(threshold)} cart to unlock 10% off.`;
      detail.textContent = 'Start with a solo-unlock bestseller or choose a fit-safe bundle below.';
      cta.textContent = 'Shop eligible picks'; cta.href = `#${root.querySelector('.tld-deals')?.id || ''}`;
    } else if (gap > 0) {
      copy.textContent = `You’re ${formatMoney(gap)} away from the extra 10% off.`;
      detail.textContent = `${formatMoney(eligible)} in eligible picks is currently in your cart.`;
      cta.textContent = 'Complete my cart'; cta.href = `#${root.querySelector('.tld-bundles')?.getAttribute('aria-labelledby') || ''}`;
    } else {
      copy.textContent = 'Extra 10% unlocked—your eligible cart is ready.';
      detail.textContent = `${formatMoney(eligible)} in eligible picks. Apply the code before checkout.`;
      cta.textContent = `Apply ${root.dataset.discountCode}`; cta.href = root.dataset.discountUrl;
    }
    pushEvent('tesevo_labor_day_cart_progress', {
      cart_value: Number(cart.total_price || 0) / 100, eligible_cart_value: eligible / 100,
      threshold_gap: gap / 100, threshold_unlocked: gap === 0 && eligible > 0
    });
  };

  const refreshCartProgress = async (root) => {
    try { renderCartProgress(root, await getCart(root)); }
    catch (error) {
      const copy = root.querySelector('[data-cart-progress-copy]');
      const detail = root.querySelector('[data-cart-progress-detail]');
      if (copy) copy.textContent = 'Unlock an extra 10% on $129+ in eligible picks.';
      if (detail) detail.textContent = 'Your live cart progress will appear here when shopping on TESEVO.';
    }
  };

  const initBundles = (root) => {
    root.querySelectorAll('[data-bundle-form]').forEach((form) => {
      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        const button = form.querySelector('button[type="submit"]');
        const status = form.querySelector('[data-bundle-status]');
        const items = [...form.querySelectorAll('[name$="[id]"]')].map((field) => ({ id: Number(field.value), quantity: 1 }));
        if (items.length < 2 || items.some((item) => !item.id)) return;
        button.disabled = true; button.textContent = 'Adding your bundle…'; if (status) status.textContent = '';
        try {
          const response = await fetch(root.dataset.cartAddUrl || '/cart/add.js', {
            method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items })
          });
          if (!response.ok) throw new Error(`Bundle request failed with ${response.status}`);
          pushEvent('tesevo_labor_day_bundle_add', {
            bundle_id: form.dataset.bundleId, variant_ids: items.map((item) => item.id),
            discount_code: root.dataset.discountCode, discount_auto_applied: true
          });
          if (status) status.textContent = 'Bundle added. Applying your Labor Day code…';
          await refreshCartProgress(root); window.location.assign(root.dataset.discountUrl);
        } catch (error) {
          button.disabled = false; button.textContent = `Add both & apply ${root.dataset.discountCode}`;
          if (status) status.textContent = 'We could not add this bundle. Please confirm availability on each product page.';
          pushEvent('tesevo_labor_day_bundle_error', { bundle_id: form.dataset.bundleId });
        }
      });
    });
  };

  const initStickyOffer = (root) => {
    const hero = root.querySelector('.tld-hero'); const sticky = root.querySelector('[data-sticky-offer]');
    if (!hero || !sticky || !('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver(([entry]) => root.classList.toggle('tld--sticky-visible', !entry.isIntersecting), { threshold: 0.02 });
    observer.observe(hero);
  };
  const initFaq = (root) => root.querySelectorAll('.tld-faq details').forEach((item) => item.addEventListener('toggle', () => {
    if (!item.open) return;
    root.querySelectorAll('.tld-faq details[open]').forEach((other) => { if (other !== item) other.open = false; });
  }));
  const initAnnouncementSync = (root) => {
    const campaignMessage = `⚡ Labor Day: Extra 10% Off $129+ · Code ${root.dataset.discountCode || 'LABOR10'}`;
    document.querySelectorAll('.m-announcement-bar__item').forEach((item) => {
      const content = item.querySelector('.m-announcement-bar__content');
      if (!content || !content.textContent.includes('5% OFF Sitewide Code')) return;
      content.textContent = campaignMessage;
      const link = item.querySelector('.m-announcement-bar__link'); if (link) link.href = root.dataset.discountUrl;
    });
  };

  const initTracking = (root) => {
    root.addEventListener('click', (event) => {
      const copyButton = event.target.closest('[data-copy-code]');
      if (copyButton) { copyCode(root, copyButton); return; }
      const cta = event.target.closest('[data-tld-cta]'); if (!cta) return;
      const product = cta.closest('[data-product-handle]');
      pushEvent('tesevo_labor_day_cta', {
        cta_name: cta.dataset.tldCta, destination: cta.getAttribute('href') || null,
        product_handle: product?.dataset.productHandle || null,
        position: product ? Number(product.dataset.productPosition) : null,
        product_price: product ? Number(product.dataset.productPrice) / 100 : null,
        bundle_id: cta.closest('[data-bundle-id]')?.dataset.bundleId || null,
        discount_auto_applied: cta.hasAttribute('data-auto-discount')
      });
    });
    root.addEventListener('submit', (event) => {
      if (!event.target.matches('.tld-product__form')) return;
      pushEvent('tesevo_labor_day_quick_add', {
        variant_id: event.target.querySelector('[name="id"]')?.value || null,
        product_handle: event.target.dataset.productHandle || null,
        position: Number(event.target.dataset.productPosition || 0) || null
      });
      window.setTimeout(() => refreshCartProgress(root), 800);
    });
  };

  const init = (root) => {
    if (!root || root.dataset.tldInitialized === 'true') return;
    root.dataset.tldInitialized = 'true';
    preserveAttributionInDiscountLinks(root); initCountdown(root); initDiscountState(root); initBundles(root); initStickyOffer(root);
    initFaq(root); initAnnouncementSync(root); initTracking(root); refreshCartProgress(root);
    ['cart:updated', 'cart:refresh', 'cart-drawer:updated'].forEach((name) => document.addEventListener(name, () => refreshCartProgress(root)));
  };
  document.querySelectorAll(SELECTOR).forEach(init);
  document.addEventListener('shopify:section:load', (event) => init(event.target.querySelector(SELECTOR)));
})();
